import React, { useEffect, useMemo, useState } from "react";
import {
  Boxes,
  Calendar,
  ClipboardList,
  Globe,
  Info,
  Layers,
  Percent,
  Plus,
  Sparkles,
  Tag,
  Trash2,
  Users,
} from "lucide-react";
import {
  Affix,
  Badge,
  Btn,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormBar,
  Input,
  Select,
  ToggleRow,
} from "../../loyalty/components/primitives";
import ChipInput, { ChipScope } from "../components/chip-input";
import ProductPicker from "../components/product-picker";

const TYPE_OPTIONS = [
  { value: "free_slab",     label: "Free Slab — bill slab → free item", Icon: Layers },
  { value: "disc_material", label: "Material Discount — % off products/categories", Icon: Tag },
  { value: "disc_bill_pct", label: "Bill % — slab-based percentage off whole bill", Icon: Percent },
  { value: "disc_bill_amt", label: "Bill Flat ₹ — slab-based ₹ off whole bill", Icon: Percent },
  { value: "combo",         label: "Combo — buy bundle → discount or free item", Icon: Boxes },
  { value: "employee",      label: "Employee — staff-only verified discount", Icon: Users },
];

// Date <-> epoch-ms helpers. The engine stores BIGINT epoch milliseconds — the
// JS runtime's Date.getTime() ↔ Date constructor pair is the canonical converter.
const toInputDate = (ms) => {
  if (!ms) return "";
  const d = new Date(Number(ms));
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromInputDate = (s, endOfDay = false) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return dt.getTime();
};

const EMPTY_SCOPE = {
  state: ["ALL"],
  store_type: ["ALL"],
  store_category: ["ALL"],
  store_code: ["ALL"],
};

const defaultTypeConfig = (type) => {
  switch (type) {
    case "free_slab":
      return {
        free_slabs: [
          { from: 100, to: 500, max_mrp: 50, materials: [] },
        ],
      };
    case "disc_material":
      return {
        mat_items: [
          { kind: "product", ref_id: "", name: "", min_qty: 1, pct: 10 },
        ],
      };
    case "disc_bill_pct":
      return {
        bill_slabs: [
          { from: 500, to: 999, pct: 5 },
          { from: 1000, to: 99999, pct: 10 },
        ],
      };
    case "disc_bill_amt":
      return {
        amt_slabs: [
          { from: 500, to: 1000, amount: 50 },
        ],
      };
    case "combo":
      return {
        combo_items: [
          { product_id: "", name: "", req_qty: 1 },
          { product_id: "", name: "", req_qty: 1 },
        ],
        benefit: {
          benefit_type: "percent",
          value: 10,
          free_product_id: null,
          incremental: false,
        },
      };
    case "employee":
      return {
        emp_scope: "uniform",
        min_bill: 500,
        disc_type: "percent",
        disc_value: 10,
        flat_amount: 0,
        max_monthly: 500,
        otp_required: true,
        time_window: false,
        from_time: "10:00",
        to_time: "21:00",
        designations: [],
        regions: [],
        stores: [],
        employees: [],
      };
    default:
      return {};
  }
};

// Type-specific summary string. Engine ignores this; admin UI surfaces it.
const buildSummary = (type, tc) => {
  switch (type) {
    case "free_slab":
      return `${(tc.free_slabs || []).length} slab(s) of free items`;
    case "disc_material":
      return `${(tc.mat_items || []).length} product/category rule(s)`;
    case "disc_bill_pct":
      return (tc.bill_slabs || [])
        .map((s) => `₹${s.from}–${s.to} → ${s.pct}%`)
        .join(" · ");
    case "disc_bill_amt":
      return (tc.amt_slabs || [])
        .map((s) => `₹${s.from}–${s.to} → ₹${s.amount} off`)
        .join(" · ");
    case "combo":
      return `${(tc.combo_items || []).length} item combo → ${tc.benefit?.benefit_type} ${tc.benefit?.value ?? ""}`;
    case "employee":
      return `Employee ${tc.disc_type} ${
        tc.disc_type === "percent" ? `${tc.disc_value}%` : `₹${tc.flat_amount}`
      }, min ₹${tc.min_bill}, cap ₹${tc.max_monthly}/mo`;
    default:
      return "";
  }
};

const RowList = ({ items, onChange, render, addLabel, defaultItem, readOnly, emptyHint }) => (
  <div className="space-y-2">
    {(items || []).map((row, idx) => (
      <div
        key={idx}
        className="flex items-start gap-2 p-3 bg-[#FAFBFC] border border-[#EEF1F5] rounded-md"
      >
        <div className="flex-1">{render(row, idx)}</div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onChange(items.filter((_, i) => i !== idx))}
            className="text-[#94A3B8] hover:text-[#E11D48] p-1"
            title="Remove"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    ))}
    {(items || []).length === 0 && (
      <div className="text-[12px] text-[#94A3B8] py-2">{emptyHint || "No entries yet."}</div>
    )}
    {!readOnly && (
      <Btn
        size="sm"
        variant="ghost"
        onClick={() => onChange([...(items || []), defaultItem()])}
      >
        <Plus size={13} />
        {addLabel}
      </Btn>
    )}
  </div>
);

const CreateTab = ({ readOnly, saving, editing, onCancel, onSave }) => {
  const today = new Date();
  const inMonth = new Date(today.getTime() + 30 * 24 * 3600 * 1000);

  const isEdit = !!editing?.id;

  const [name, setName] = useState(editing?.name || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [status, setStatus] = useState(editing?.status || "draft");
  const [type, setType] = useState(editing?.type || "disc_bill_pct");
  const [priority, setPriority] = useState(editing?.priority ?? 100);
  const [returnable, setReturnable] = useState(
    editing?.returnable !== undefined ? !!editing.returnable : true
  );
  const [fromDate, setFromDate] = useState(
    editing?.from_date ?? today.getTime()
  );
  const [toDate, setToDate] = useState(editing?.to_date ?? inMonth.getTime());

  const [scope, setScope] = useState(editing?.config?.scope || EMPTY_SCOPE);
  const [eligibleBatches, setEligibleBatches] = useState(
    editing?.config?.eligible_batches || []
  );
  const [applyOneTime, setApplyOneTime] = useState(
    !!editing?.config?.apply_one_time
  );

  const [typeConfig, setTypeConfig] = useState(() =>
    editing?.config?.type_config || defaultTypeConfig(editing?.type || "disc_bill_pct")
  );

  // Re-hydrate when the editing prop changes (e.g. cashier clicks a different
  // row without leaving the tab). useEffect guards against missing prop edge
  // cases — when editing is null we leave whatever the user has been typing.
  useEffect(() => {
    if (!editing) return;
    setName(editing.name || "");
    setDescription(editing.description || "");
    setStatus(editing.status || "draft");
    setType(editing.type || "disc_bill_pct");
    setPriority(editing.priority ?? 100);
    setReturnable(editing.returnable !== undefined ? !!editing.returnable : true);
    setFromDate(editing.from_date ?? today.getTime());
    setToDate(editing.to_date ?? inMonth.getTime());
    setScope(editing.config?.scope || EMPTY_SCOPE);
    setEligibleBatches(editing.config?.eligible_batches || []);
    setApplyOneTime(!!editing.config?.apply_one_time);
    setTypeConfig(
      editing.config?.type_config || defaultTypeConfig(editing.type || "disc_bill_pct")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const onTypeChange = (next) => {
    setType(next);
    setTypeConfig(defaultTypeConfig(next));
  };

  const setTC = (patch) => setTypeConfig((c) => ({ ...c, ...patch }));

  const summary = useMemo(() => buildSummary(type, typeConfig), [type, typeConfig]);

  const canSave = name.trim() && fromDate && toDate && toDate >= fromDate;

  const handleSave = () => {
    if (!canSave || readOnly) return;
    onSave?.({
      name: name.trim(),
      description: description.trim() || null,
      status,
      type,
      from_date: fromDate,
      to_date: toDate,
      returnable,
      priority: Number(priority) || 0,
      config: {
        scope,
        eligible_batches: eligibleBatches,
        apply_one_time: applyOneTime,
        summary,
        type_config: typeConfig,
      },
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">New scheme</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[720px]">
            One form covers all six scheme types — the type-specific section
            below switches based on what you pick.
          </p>
        </div>
      </div>

      {/* Header card */}
      <Card>
        <CardHeader
          icon={<ClipboardList size={18} />}
          tone="blue"
          title="Basics"
          desc="Name, description, lifecycle status, type and priority"
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Monsoon Big Bill"
                disabled={readOnly}
              />
            </Field>
            <Field label="Status">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={readOnly}
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </Select>
            </Field>
            <Field label="Description" className="md:col-span-2">
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Internal note for staff"
                disabled={readOnly}
              />
            </Field>
            <Field label="Type" required help="The type sets the form below.">
              <Select
                value={type}
                onChange={(e) => onTypeChange(e.target.value)}
                disabled={readOnly}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Priority"
              help="Higher = applied first. Ties: oldest scheme wins."
            >
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                disabled={readOnly}
              />
            </Field>
          </div>

          <div className="mt-4">
            <ToggleRow
              title="Returnable"
              desc="When OFF, bills carrying this scheme are blocked from return."
              on={returnable}
              onChange={setReturnable}
              disabled={readOnly}
            />
          </div>
        </CardBody>
      </Card>

      {/* Validity window */}
      <Card>
        <CardHeader
          icon={<Calendar size={18} />}
          tone="green"
          title="Validity window"
          desc="Stored as BIGINT epoch milliseconds — same as Date.now() output."
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="From" required>
              <Input
                type="date"
                value={toInputDate(fromDate)}
                onChange={(e) =>
                  setFromDate(fromInputDate(e.target.value, false) || fromDate)
                }
                disabled={readOnly}
              />
            </Field>
            <Field label="To" required>
              <Input
                type="date"
                value={toInputDate(toDate)}
                onChange={(e) =>
                  setToDate(fromInputDate(e.target.value, true) || toDate)
                }
                disabled={readOnly}
              />
            </Field>
          </div>
          {toDate < fromDate && (
            <div className="text-[12px] text-[#B91C1C] mt-2">
              To-date must be on or after the from-date.
            </div>
          )}
        </CardBody>
      </Card>

      {/* Scope */}
      <Card>
        <CardHeader
          icon={<Globe size={18} />}
          tone="purple"
          title="Scope"
          desc='Leave any axis as ["ALL"] to apply everywhere.'
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChipScope
              label="State"
              value={scope.state}
              onChange={(v) => setScope({ ...scope, state: v })}
              placeholder="e.g. TS, AP"
              disabled={readOnly}
            />
            <ChipScope
              label="Store type"
              value={scope.store_type}
              onChange={(v) => setScope({ ...scope, store_type: v })}
              placeholder="e.g. retail, b2b"
              disabled={readOnly}
            />
            <ChipScope
              label="Store category"
              value={scope.store_category}
              onChange={(v) => setScope({ ...scope, store_category: v })}
              placeholder="e.g. tier-1"
              disabled={readOnly}
            />
            <ChipScope
              label="Store code"
              value={scope.store_code}
              onChange={(v) => setScope({ ...scope, store_code: v })}
              placeholder="e.g. ST-0001"
              disabled={readOnly}
            />
          </div>
        </CardBody>
      </Card>

      {/* Behaviour */}
      <Card>
        <CardHeader
          icon={<Sparkles size={18} />}
          tone="amber"
          title="Behaviour"
          desc="One-time semantics and batch eligibility"
        />
        <CardBody>
          <ToggleRow
            title="One-time per customer"
            desc="Engine skips this scheme if the customer has already redeemed it."
            on={applyOneTime}
            onChange={setApplyOneTime}
            disabled={readOnly}
          />
          <div className="pt-4">
            <Field
              label="Eligible batches"
              help="Empty = all batches. Add batch codes to restrict."
            >
              <ChipInput
                values={eligibleBatches}
                onChange={setEligibleBatches}
                placeholder="e.g. B-2406A"
                disabled={readOnly}
                mono
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* Type-specific */}
      <Card>
        <CardHeader
          icon={
            type === "combo" ? (
              <Boxes size={18} />
            ) : type === "employee" ? (
              <Users size={18} />
            ) : type === "free_slab" ? (
              <Layers size={18} />
            ) : (
              <Percent size={18} />
            )
          }
          tone="teal"
          title={`Type configuration · ${
            TYPE_OPTIONS.find((t) => t.value === type)?.label.split(" — ")[0]
          }`}
          desc={summary || "Fill in the rules below"}
        />
        <CardBody>
          {type === "free_slab" && (
            <FreeSlabSection
              tc={typeConfig}
              setTC={setTC}
              readOnly={readOnly}
            />
          )}
          {type === "disc_material" && (
            <DiscMaterialSection
              tc={typeConfig}
              setTC={setTC}
              readOnly={readOnly}
            />
          )}
          {type === "disc_bill_pct" && (
            <DiscBillPctSection
              tc={typeConfig}
              setTC={setTC}
              readOnly={readOnly}
            />
          )}
          {type === "disc_bill_amt" && (
            <DiscBillAmtSection
              tc={typeConfig}
              setTC={setTC}
              readOnly={readOnly}
            />
          )}
          {type === "combo" && (
            <ComboSection tc={typeConfig} setTC={setTC} readOnly={readOnly} />
          )}
          {type === "employee" && (
            <EmployeeSection
              tc={typeConfig}
              setTC={setTC}
              readOnly={readOnly}
            />
          )}
        </CardBody>
      </Card>

      <Callout tone="blue" icon={<Info size={16} />}>
        On save we POST <code className="font-mono text-[12px]">/api/schemes</code> with
        a single payload — header fields flat, everything else nested under
        <code className="font-mono text-[12px] mx-1">config</code>. The summary above
        is regenerated server-side; the value sent here is a hint only.
      </Callout>

      {!readOnly && (
        <FormBar
          hint="Drafts don't run at POS. Set status to Active or Scheduled to publish."
          onCancel={onCancel}
          onSave={handleSave}
          saveLabel={isEdit ? "Update scheme" : "Create scheme"}
          saving={saving}
          disabled={!canSave}
        />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// 6.1 free_slab
// ──────────────────────────────────────────────────────────────────────
const FreeSlabSection = ({ tc, setTC, readOnly }) => (
  <RowList
    items={tc.free_slabs}
    onChange={(next) => setTC({ free_slabs: next })}
    addLabel="Add slab"
    emptyHint="No slabs configured — add at least one."
    defaultItem={() => ({ from: 0, to: 0, max_mrp: 0, materials: [] })}
    readOnly={readOnly}
    render={(slab, idx) => {
      const update = (patch) => {
        const next = tc.free_slabs.map((s, i) => (i === idx ? { ...s, ...patch } : s));
        setTC({ free_slabs: next });
      };
      const setMaterials = (mats) => update({ materials: mats });
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Bill from ₹">
              <Input
                type="number"
                value={slab.from}
                onChange={(e) => update({ from: Number(e.target.value) })}
                disabled={readOnly}
              />
            </Field>
            <Field label="Bill to ₹">
              <Input
                type="number"
                value={slab.to}
                onChange={(e) => update({ to: Number(e.target.value) })}
                disabled={readOnly}
              />
            </Field>
            <Field label="Max MRP of free item">
              <Affix pre="₹">
                <Input
                  type="number"
                  value={slab.max_mrp}
                  onChange={(e) => update({ max_mrp: Number(e.target.value) })}
                  style={{ paddingLeft: 28 }}
                  disabled={readOnly}
                />
              </Affix>
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[12px] font-semibold text-[#334155]">
                Free item products
              </label>
              <Badge tone="slate">{slab.materials?.length || 0}</Badge>
            </div>
            <div className="space-y-1.5">
              {(slab.materials || []).map((m, i) => (
                <ProductPicker
                  key={m.product_id || `new-${i}`}
                  value={m.product_id ? { product_id: m.product_id, item_code: m.item_code, name: m.name } : null}
                  disabled={readOnly}
                  onSelect={(picked) => {
                    if (!picked) {
                      // clear-only on the slot — drop the row
                      setMaterials(
                        (slab.materials || []).filter((_, idx) => idx !== i)
                      );
                      return;
                    }
                    const next = [...(slab.materials || [])];
                    next[i] = {
                      product_id: picked.product_id,
                      item_code: picked.item_code,
                      name: picked.name,
                      mrp: picked.mrp,
                    };
                    setMaterials(next);
                  }}
                  onClear={() => {
                    setMaterials(
                      (slab.materials || []).filter((_, idx) => idx !== i)
                    );
                  }}
                />
              ))}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() =>
                    setMaterials([
                      ...(slab.materials || []),
                      { product_id: "", name: "", mrp: 0 },
                    ])
                  }
                  className="text-[12px] font-semibold text-primary hover:underline"
                >
                  + Add product
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }}
  />
);

// ──────────────────────────────────────────────────────────────────────
// 6.2 disc_material
// ──────────────────────────────────────────────────────────────────────
const DiscMaterialSection = ({ tc, setTC, readOnly }) => (
  <RowList
    items={tc.mat_items}
    onChange={(next) => setTC({ mat_items: next })}
    addLabel="Add material rule"
    defaultItem={() => ({
      kind: "product",
      ref_id: "",
      name: "",
      min_qty: 1,
      pct: 10,
    })}
    readOnly={readOnly}
    render={(row, idx) => {
      const update = (patch) => {
        const next = tc.mat_items.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        setTC({ mat_items: next });
      };
      return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Kind">
            <Select
              value={row.kind}
              onChange={(e) => update({ kind: e.target.value })}
              disabled={readOnly}
            >
              <option value="product">Product</option>
              <option value="category">Category</option>
            </Select>
          </Field>
          <Field label={row.kind === "product" ? "Product" : "Reference id"}>
            {row.kind === "product" ? (
              <ProductPicker
                value={row.ref_id ? { product_id: row.ref_id, name: row.name } : null}
                disabled={readOnly}
                onSelect={(picked) =>
                  picked
                    ? update({ ref_id: picked.product_id, name: picked.name })
                    : update({ ref_id: "", name: "" })
                }
                onClear={() => update({ ref_id: "", name: "" })}
              />
            ) : (
              <Input
                value={row.ref_id}
                onChange={(e) => update({ ref_id: e.target.value })}
                placeholder="Category id"
                disabled={readOnly}
                mono
              />
            )}
          </Field>
          <Field label="Display name">
            <Input
              value={row.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder={row.kind === "product" ? "Auto-filled from product" : "Antibiotics"}
              disabled={readOnly}
            />
          </Field>
          <Field label="Min qty">
            <Input
              type="number"
              value={row.min_qty}
              onChange={(e) => update({ min_qty: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Discount">
            <Affix post="%">
              <Input
                type="number"
                value={row.pct}
                onChange={(e) => update({ pct: Number(e.target.value) })}
                disabled={readOnly}
              />
            </Affix>
          </Field>
        </div>
      );
    }}
  />
);

// ──────────────────────────────────────────────────────────────────────
// 6.3 disc_bill_pct
// ──────────────────────────────────────────────────────────────────────
const DiscBillPctSection = ({ tc, setTC, readOnly }) => (
  <RowList
    items={tc.bill_slabs}
    onChange={(next) => setTC({ bill_slabs: next })}
    addLabel="Add slab"
    defaultItem={() => ({ from: 0, to: 0, pct: 0 })}
    readOnly={readOnly}
    render={(slab, idx) => {
      const update = (patch) => {
        const next = tc.bill_slabs.map((s, i) => (i === idx ? { ...s, ...patch } : s));
        setTC({ bill_slabs: next });
      };
      return (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Bill from ₹">
            <Input
              type="number"
              value={slab.from}
              onChange={(e) => update({ from: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Bill to ₹">
            <Input
              type="number"
              value={slab.to}
              onChange={(e) => update({ to: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Discount">
            <Affix post="%">
              <Input
                type="number"
                value={slab.pct}
                onChange={(e) => update({ pct: Number(e.target.value) })}
                disabled={readOnly}
              />
            </Affix>
          </Field>
        </div>
      );
    }}
  />
);

// ──────────────────────────────────────────────────────────────────────
// 6.4 disc_bill_amt
// ──────────────────────────────────────────────────────────────────────
const DiscBillAmtSection = ({ tc, setTC, readOnly }) => (
  <RowList
    items={tc.amt_slabs}
    onChange={(next) => setTC({ amt_slabs: next })}
    addLabel="Add slab"
    defaultItem={() => ({ from: 0, to: 0, amount: 0 })}
    readOnly={readOnly}
    render={(slab, idx) => {
      const update = (patch) => {
        const next = tc.amt_slabs.map((s, i) => (i === idx ? { ...s, ...patch } : s));
        setTC({ amt_slabs: next });
      };
      return (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Bill from ₹">
            <Input
              type="number"
              value={slab.from}
              onChange={(e) => update({ from: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Bill to ₹">
            <Input
              type="number"
              value={slab.to}
              onChange={(e) => update({ to: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Flat off">
            <Affix pre="₹">
              <Input
                type="number"
                value={slab.amount}
                onChange={(e) => update({ amount: Number(e.target.value) })}
                style={{ paddingLeft: 28 }}
                disabled={readOnly}
              />
            </Affix>
          </Field>
        </div>
      );
    }}
  />
);

// ──────────────────────────────────────────────────────────────────────
// 6.5 combo
// ──────────────────────────────────────────────────────────────────────
const ComboSection = ({ tc, setTC, readOnly }) => {
  const benefit = tc.benefit || {
    benefit_type: "percent",
    value: 0,
    free_product_id: null,
    incremental: false,
  };
  const setBenefit = (patch) => setTC({ benefit: { ...benefit, ...patch } });

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] font-bold tracking-wider uppercase text-[#64748B] mb-2">
          Combo items (all required)
        </div>
        <RowList
          items={tc.combo_items}
          onChange={(next) => setTC({ combo_items: next })}
          addLabel="Add combo item"
          defaultItem={() => ({ product_id: "", name: "", req_qty: 1 })}
          readOnly={readOnly}
          render={(row, idx) => {
            const update = (patch) => {
              const next = tc.combo_items.map((r, i) =>
                i === idx ? { ...r, ...patch } : r
              );
              setTC({ combo_items: next });
            };
            return (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Product">
                  <ProductPicker
                    value={row.product_id ? { product_id: row.product_id, name: row.name } : null}
                    disabled={readOnly}
                    onSelect={(picked) =>
                      picked
                        ? update({ product_id: picked.product_id, name: picked.name })
                        : update({ product_id: "", name: "" })
                    }
                    onClear={() => update({ product_id: "", name: "" })}
                  />
                </Field>
                <Field label="Display name">
                  <Input
                    value={row.name}
                    onChange={(e) => update({ name: e.target.value })}
                    placeholder="Auto-filled"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Required qty">
                  <Input
                    type="number"
                    value={row.req_qty}
                    onChange={(e) => update({ req_qty: Number(e.target.value) })}
                    disabled={readOnly}
                  />
                </Field>
              </div>
            );
          }}
        />
      </div>

      <div>
        <div className="text-[12px] font-bold tracking-wider uppercase text-[#64748B] mb-2">
          Benefit
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Benefit type">
            <Select
              value={benefit.benefit_type}
              onChange={(e) => setBenefit({ benefit_type: e.target.value })}
              disabled={readOnly}
            >
              <option value="percent">Percent off</option>
              <option value="flat">Flat ₹ off</option>
              <option value="price">Fixed price</option>
              <option value="free">Free product</option>
            </Select>
          </Field>
          <Field label="Value">
            <Input
              type="number"
              value={benefit.value}
              onChange={(e) => setBenefit({ value: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Field>
          {benefit.benefit_type === "free" && (
            <Field label="Free product">
              <ProductPicker
                value={benefit.free_product_id ? { product_id: benefit.free_product_id, name: benefit.free_product_name } : null}
                disabled={readOnly}
                onSelect={(picked) =>
                  picked
                    ? setBenefit({ free_product_id: picked.product_id, free_product_name: picked.name })
                    : setBenefit({ free_product_id: null, free_product_name: "" })
                }
                onClear={() => setBenefit({ free_product_id: null, free_product_name: "" })}
              />
            </Field>
          )}
        </div>
        <div className="mt-3">
          <ToggleRow
            title="Incremental"
            desc="Apply once per combo set found in the bill (e.g. 3× items → 3× benefit)."
            on={!!benefit.incremental}
            onChange={(v) => setBenefit({ incremental: v })}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// 6.6 employee
// ──────────────────────────────────────────────────────────────────────
const EmployeeSection = ({ tc, setTC, readOnly }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <Badge tone="amber">OTP integration coming in v2</Badge>
      <span className="text-[12px] text-[#64748B]">
        Until the dedicated staff path lands, this scheme routes through the
        existing loyalty OTP flow.
      </span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Employee scope">
        <Select
          value={tc.emp_scope}
          onChange={(e) => setTC({ emp_scope: e.target.value })}
          disabled={readOnly}
        >
          <option value="uniform">Uniform (all employees)</option>
          <option value="designation">By designation</option>
          <option value="region">By region</option>
          <option value="store">By store</option>
          <option value="employee">Specific employees</option>
        </Select>
      </Field>
      <Field label="Min bill">
        <Affix pre="₹">
          <Input
            type="number"
            value={tc.min_bill}
            onChange={(e) => setTC({ min_bill: Number(e.target.value) })}
            style={{ paddingLeft: 28 }}
            disabled={readOnly}
          />
        </Affix>
      </Field>
      <Field label="Discount type">
        <Select
          value={tc.disc_type}
          onChange={(e) => setTC({ disc_type: e.target.value })}
          disabled={readOnly}
        >
          <option value="percent">Percent</option>
          <option value="flat">Flat ₹</option>
        </Select>
      </Field>
      {tc.disc_type === "percent" ? (
        <Field label="Discount %">
          <Affix post="%">
            <Input
              type="number"
              value={tc.disc_value}
              onChange={(e) => setTC({ disc_value: Number(e.target.value) })}
              disabled={readOnly}
            />
          </Affix>
        </Field>
      ) : (
        <Field label="Flat amount">
          <Affix pre="₹">
            <Input
              type="number"
              value={tc.flat_amount}
              onChange={(e) => setTC({ flat_amount: Number(e.target.value) })}
              style={{ paddingLeft: 28 }}
              disabled={readOnly}
            />
          </Affix>
        </Field>
      )}
      <Field label="Monthly cap per employee">
        <Affix pre="₹">
          <Input
            type="number"
            value={tc.max_monthly}
            onChange={(e) => setTC({ max_monthly: Number(e.target.value) })}
            style={{ paddingLeft: 28 }}
            disabled={readOnly}
          />
        </Affix>
      </Field>
    </div>

    <div className="pt-1">
      <ToggleRow
        title="OTP required"
        desc="Cashier must verify the employee via OTP before applying."
        on={!!tc.otp_required}
        onChange={(v) => setTC({ otp_required: v })}
        disabled={readOnly}
      />
      <ToggleRow
        title="Time window"
        desc="Only apply within the configured hours of the day."
        on={!!tc.time_window}
        onChange={(v) => setTC({ time_window: v })}
        disabled={readOnly}
      />
    </div>

    {tc.time_window && (
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Field label="From">
          <Input
            type="time"
            value={tc.from_time}
            onChange={(e) => setTC({ from_time: e.target.value })}
            disabled={readOnly}
          />
        </Field>
        <Field label="To">
          <Input
            type="time"
            value={tc.to_time}
            onChange={(e) => setTC({ to_time: e.target.value })}
            disabled={readOnly}
          />
        </Field>
      </div>
    )}

    {tc.emp_scope === "designation" && (
      <Field label="Designations">
        <ChipInput
          values={tc.designations || []}
          onChange={(v) => setTC({ designations: v })}
          placeholder="e.g. pharmacist"
          disabled={readOnly}
        />
      </Field>
    )}
    {tc.emp_scope === "region" && (
      <Field label="Regions">
        <ChipInput
          values={tc.regions || []}
          onChange={(v) => setTC({ regions: v })}
          placeholder="e.g. south"
          disabled={readOnly}
        />
      </Field>
    )}
    {tc.emp_scope === "store" && (
      <Field label="Stores">
        <ChipInput
          values={tc.stores || []}
          onChange={(v) => setTC({ stores: v })}
          placeholder="e.g. ST-0001"
          disabled={readOnly}
          mono
        />
      </Field>
    )}
    {tc.emp_scope === "employee" && (
      <Field label="Employee ids">
        <ChipInput
          values={tc.employees || []}
          onChange={(v) => setTC({ employees: v })}
          placeholder="ULID"
          disabled={readOnly}
          mono
        />
      </Field>
    )}
  </div>
);

export default CreateTab;
